import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3Icon, HandCoinsIcon, RecycleIcon, SearchIcon, SparklesIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const MAIN_BENEFITS = [{
  label: 'See what works',
  detail: 'Across brands, countries and channels.',
  icon: BarChart3Icon,
  image: 'https://cdn.magicpatterns.com/uploads/kAPX1sS6y8zcjhhHD6CLzo/Screenshot_2026-09-21_at_13.14.09.png',
  alt: 'Portfolio overview comparing gap severity by market and product'
}, {
  label: 'Learn faster',
  detail: 'Apply success from one market to another.',
  icon: SparklesIcon,
  image: 'https://cdn.magicpatterns.com/uploads/3feRkRv9AX1mR6BMuNMFrM/Screenshot_2026-09-21_at_13.13.56.png',
  alt: 'Single product view showing performance across every assessed market'
}] as const;
const SUPPORTING_BENEFITS = [{
  label: 'Reuse more',
  detail: 'Adapt instead of rebuild.',
  icon: RecycleIcon
}, {
  label: 'Spot opportunities',
  detail: 'Identify where proven ideas can travel.',
  icon: SearchIcon
}, {
  label: 'Build buying power',
  detail: 'More volume creates lower unit cost.',
  icon: HandCoinsIcon
}] as const;
export function SlideSharedAdvantage({}: SlideProps) {
  return <InteriorSlide banner={<SlideBanner data-id="element-146" data-mp="1jusne7pgtyb5">
          Centralise the visibility, learning and buying power — not every
          decision.
        </SlideBanner>} data-id="element-145" data-mp="2gm49pososqb4">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex flex-shrink-0 flex-col gap-[clamp(0.5rem,1vw,1rem)] lg:flex-row lg:items-end lg:justify-between lg:gap-10" data-id="element-147" data-mp="1ov7zkvy0gzrj">
        <div className="min-w-0" data-id="element-148" data-mp="349cem5m7dxcm">
          <p className="mb-2 text-[clamp(0.8rem,1.15vw,1.05rem)] font-semibold italic leading-snug text-[var(--muted)]" data-id="element-149" data-mp="398ytejhojlnj">
            “Snakker seems to be popping up everywhere.”
          </p>
          <h2 className="text-[clamp(1.7rem,3.2vw,2.9rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-150" data-mp="198k4qb9xhd97">
            Good. Now let’s make that{' '}
            <span className="text-[var(--brand)]" data-id="element-151" data-mp="1j4dfbp436woc">activity compound.</span>
          </h2>
        </div>
        <p className="max-w-sm flex-shrink-0 border-l-2 border-[var(--brand)] pl-4 text-[clamp(0.8rem,1.05vw,0.98rem)] font-semibold leading-snug text-[var(--fg)] lg:border-l-0 lg:border-r-2 lg:pl-0 lg:pr-4 lg:text-right" data-id="element-152" data-mp="1e6byzby1pbgk">
          A central overview gives LEO something individual campaigns cannot.
        </p>
      </motion.header>

      <div className="mt-[clamp(0.8rem,1.6vw,1.4rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.6rem,1.2vw,1.1rem)] lg:grid-cols-2" data-id="element-153" data-mp="vfb0sq4r1jy2">
        {MAIN_BENEFITS.map((benefit, index) => <motion.figure key={benefit.label} initial={{
        opacity: 0,
        y: 22
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.18 + index * 0.1,
        duration: 0.5
      }} className="flex min-h-0 flex-col rounded-2xl bg-[var(--surface)] p-[clamp(0.7rem,1.2vw,1.1rem)]" data-id="element-154" data-mp="7ndn0thqohub" data-mpi={index}>
            <figcaption className="flex flex-shrink-0 items-center gap-[clamp(0.5rem,0.9vw,0.8rem)]" data-id="element-155" data-mp="25e3o6w5o3b7n">
              <span className="flex h-[clamp(1.9rem,2.4vw,2.4rem)] w-[clamp(1.9rem,2.4vw,2.4rem)] flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-156" data-mp="29e4fhhdv7gw4">
                <benefit.icon className="h-[clamp(0.9rem,1.2vw,1.15rem)] w-[clamp(0.9rem,1.2vw,1.15rem)] text-white" aria-hidden="true" data-id="element-157" data-mp="323l03sv8tths" />
              </span>
              <span className="min-w-0" data-id="element-158" data-mp="2t2mzlkk0k43y">
                <span className="block text-[clamp(0.95rem,1.4vw,1.35rem)] font-extrabold uppercase leading-tight tracking-[-0.02em] text-[var(--fg)]" data-id="element-159" data-mp="2t1bspbv9ug8m">
                  {benefit.label}
                </span>
                <span className="block text-[clamp(0.72rem,0.98vw,0.92rem)] leading-snug text-[var(--muted)]" data-id="element-160" data-mp="1hp19nx6bxyhd">
                  {benefit.detail}
                </span>
              </span>
            </figcaption>

            <div className="mt-[clamp(0.5rem,0.9vw,0.8rem)] flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-white p-1.5 ring-1 ring-[var(--fg)]/10" data-id="element-161" data-mp="1s4jy6yf9q518">
              <img src={benefit.image} alt={benefit.alt} className="max-h-full w-full rounded-lg object-contain" data-id="element-162" data-mp="1j7jev6722x4n" />
            </div>
          </motion.figure>)}
      </div>

      <ul className="mt-[clamp(0.6rem,1.1vw,1rem)] grid flex-shrink-0 grid-cols-1 gap-[clamp(0.5rem,1vw,0.9rem)] sm:grid-cols-3" data-id="element-163" data-mp="26vj5m64danj8">
        {SUPPORTING_BENEFITS.map((benefit, index) => <motion.li key={benefit.label} initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.42 + index * 0.08,
        duration: 0.45
      }} className="flex items-center gap-[clamp(0.5rem,0.9vw,0.8rem)] rounded-2xl bg-[var(--surface)] px-[clamp(0.7rem,1.2vw,1.1rem)] py-[clamp(0.5rem,0.9vw,0.8rem)]" data-id="element-164" data-mp="21ysr3n87nzzx" data-mpi={index}>
            <benefit.icon className="h-[clamp(1rem,1.3vw,1.25rem)] w-[clamp(1rem,1.3vw,1.25rem)] flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-165" data-mp="371d22nmbgc61" />
            <span className="min-w-0" data-id="element-166" data-mp="3fy5i9fo7jt4m">
              <span className="block text-[clamp(0.78rem,1.05vw,1rem)] font-extrabold uppercase leading-tight tracking-[-0.01em] text-[var(--fg)]" data-id="element-167" data-mp="3kvxcigcbrrbz">
                {benefit.label}
              </span>
              <span className="block text-[clamp(0.68rem,0.92vw,0.85rem)] leading-snug text-[var(--muted)]" data-id="element-168" data-mp="3ut20mlpqdwvf">
                {benefit.detail}
              </span>
            </span>
          </motion.li>)}
      </ul>
    </InteriorSlide>;
}